import { Box, Columns, Column, useDialogControllers, useToast } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'

import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useHistory } from 'react-router'
import { Userpilot } from 'userpilot'
import PageTitleBar from '@/components/page-title'
import { useAvailableCreditChecks } from '@/hooks/use-block-and-pay'
import { useGetStandAloneTrustScore } from '@/hooks/use-standalone-trust-score'
import useLocalStorage from '@/hooks/use-localstorage'

import NoBillingUserDialog from '@/features/SponsoredListing/modals/no-billing-user-modal'
import useCurrentSession from '@/hooks/use-current-session'
import { IdVerificationPendingModal, IdVerificationRequiredModal } from './modal'

import CreditCheckForm from './credit-check-form'
import TenantScreeningForm from './tenant-screening-form'
import PaymentForm from './payment-form'
import Confirmation from './confirmation'
import FormStepper from './form-stepper'
import { FormProvider } from './form-context'
import { useUserIdState } from './hook/useUserIdState'

export const FORM_STEP_NUMBER: { [key: number]: string } = {
  1: 'credit-check',
  2: 'tenant-screening',
  3: 'payment',
  4: 'confirmation'
}

const Form = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const session = useCurrentSession()
  const { formType, trustScoreId = '' } = useParams<{ formType: string; trustScoreId: string }>()
  const { data, isLoading, refetch, error } = useGetStandAloneTrustScore(trustScoreId)
  const { isUserIdUnverified, isUserIdPending, isUserIdVerified } = useUserIdState()

  const showToast = useToast()

  const [, setLeftFormType] = useLocalStorage(
    `standalone-trustscore-${trustScoreId}`,
    'credit-check'
  )

  const { visible, showModal, closeModal } = useDialogControllers({
    verificationRequired: false,
    verificationPending: false,
    billingUser: false
  })

  const notAllowedError = error?.message === 'access denied'
  useEffect(() => {
    if (notAllowedError) {
      history.replace('/trust-score')
      showToast({
        message: error?.message,
        tone: 'critical'
      })
    }
  }, [notAllowedError, history, error?.message, showToast])

  const FORM_LIST: { [key: string]: any } = {
    'credit-check': CreditCheckForm,
    'tenant-screening': TenantScreeningForm,
    payment: PaymentForm,
    confirmation: Confirmation
  }

  const activeStep = Object.keys(FORM_STEP_NUMBER)
    .map(key => Number(key))
    .find(key => FORM_STEP_NUMBER[key] === formType)!

  const FormComponent = FORM_LIST[FORM_STEP_NUMBER[activeStep]]

  const { availableCreditChecks } = useAvailableCreditChecks()

  const hasAvailableCreditChecks = availableCreditChecks > 0

  useEffect(() => {
    if (isUserIdVerified) return

    if (isUserIdPending) {
      return showModal('verificationPending')()
    }

    if (isUserIdUnverified) {
      return showModal('verificationRequired')()
    }
  }, [isUserIdUnverified, isUserIdVerified, isUserIdPending])

  useEffect(() => {
    trustScoreId && setLeftFormType(formType)

    if (!trustScoreId && formType !== 'credit-check') {
      history.replace('/trust-score/credit-check')
    }

    if (formType === 'payment' && hasAvailableCreditChecks) {
      trustScoreId
        ? history.replace(`/trust-score/edit/${trustScoreId}/credit-check`)
        : history.replace('/trust-score/credit-check')
    }
  }, [formType, history, hasAvailableCreditChecks, trustScoreId])

  useEffect(() => {
    if (!session?.billing_user_valid) return showModal('billingUser')()
  }, [session?.billing_user_valid])

  const handleCloseBillingUserModal = () => {
    history.replace('/trust-score')
    closeModal('billingUser')()
  }

  const handleCloseRequiredModal = () => {
    Userpilot.track('app_standalone_ts_idverify_close_btn')
    closeModal('verificationRequired')()
    closeModal('verificationPending')()
  }
  const handleSubmitRequiredModal = () => {
    Userpilot.track('app_standalone_ts_idverify_gotit_btn')
    closeModal('verificationRequired')()
    closeModal('verificationPending')()
  }
  const handleSubmitPendingModal = () => {
    Userpilot.track('app_standalone_ts_idverify_inprogress_gotit_btn')
    closeModal('verificationPending')()
  }
  const handleClosePendingModal = () => {
    Userpilot.track('app_standalone_ts_idverify_inprogress_close_btn')
    closeModal('verificationPending')()
  }
  return (
    <>
      <PageTitleBar customChild={null}>
        {t('l.standalone_trustscore.list.title', 'Trust Score')}
      </PageTitleBar>
      <NoBillingUserDialog open={visible.billingUser} onClose={handleCloseBillingUserModal} />
      <FormProvider>
        <IdVerificationPendingModal
          visible={visible.verificationPending}
          onClose={handleClosePendingModal}
          onSubmit={handleSubmitPendingModal}
        />
        <IdVerificationRequiredModal
          visible={visible.verificationRequired}
          onClose={handleCloseRequiredModal}
          onSubmit={handleSubmitRequiredModal}
        />
        <Box
          paddingY={{ mobile: 'gutter', tablet: 'xxxlarge' }}
          paddingX={{ mobile: 'none', tablet: 'xxxlarge' }}
          background="neutralLight"
          height="full"
          display="flex"
          justifyContent="center"
          alignItems="flexStart"
          flexDirection="row"
        >
          <Box style={{ maxWidth: '916px', width: '100%' }}>
            <Columns
              space={{ mobile: 'none', tablet: 'gutter' }}
              alignY={'top'}
              align={{ mobile: 'left', tablet: 'center' }}
              collapseBelow="tablet"
            >
              <Column width="content">
                <FormStepper data={data} isLoading={isLoading} />
              </Column>
              <Column>
                <Box marginBottom={{ tablet: 'xxxlarge', mobile: 'none' }}>
                  <FormComponent
                    data={data}
                    refetch={refetch}
                    isLoading={isLoading}
                    hasAvailableCreditChecks={hasAvailableCreditChecks}
                    availableCreditChecks={availableCreditChecks}
                  />
                </Box>
              </Column>
            </Columns>
          </Box>
        </Box>
      </FormProvider>
    </>
  )
}

export default Form
